import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Who Owns Your Donor Data? What Missionaries Should Ask Before Choosing a Platform';
const DESCRIPTION =
    'If you ever change platforms, sending organizations, or fields, can you take your donor list with you? A practical checklist for a question most missionaries never ask until it is too late.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover9.jpg',
            datePublished: '2026-08-05',
            dateModified: '2026-08-05',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/who-owns-your-donor-data-missionary-fundraising',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Can I export my donor list if I switch fundraising platforms?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "It depends entirely on the platform's policy, and most missionaries never check until they need to leave. Ask before you sign up, in writing: can you export names, emails, and giving history to a CSV at any time, with no approval required? If the answer is unclear or 'contact support,' assume the answer is no.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Does my sending organization or agency own my donor data instead of me?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Sometimes, and it is worth clarifying directly rather than assuming. Some agencies process all giving through their own internal system, in which case the agency legally holds the donor relationship. Others let a missionary run support through an independent platform where the missionary is the account owner. Ask your agency which model they use before you build years of donor relationships on top of it.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Why does donor data ownership matter if I am not planning to leave?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Plans change more often than missionaries expect — a change of field, a change of sending agency, a platform raising fees or shutting down. The donors you spent years building trust with are the hardest asset to rebuild from scratch. Confirming you can take that list with you costs five minutes up front and protects years of relationship-building later.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What should I ask a platform about data ownership before signing up?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Three questions: Can I export my full donor list (name, email, giving history) myself, at any time, without asking support? If I close my account, is my data deleted or retained, and for how long? If the platform is acquired or shuts down, what happens to my donor list? Get the answers in writing, not just a verbal assurance.',
                    },
                },
            ],
        },
    ],
};

export default function WhoOwnsYourDonorData() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/who-owns-your-donor-data-missionary-fundraising' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-donor-data-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover9.jpg" alt="A missionary reviewing donor records on a laptop" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Most missionaries pick a fundraising platform based on fees, ease of setup, or what their sending agency already uses. Almost none ask the question that matters most a few years in: if I ever need to leave, do I get to take my donor list with me? By the time that question comes up, it's often too late to negotiate the answer.</p>

                    <h2>Why this question gets skipped</h2>

                    <p>When you're setting up support raising for the first time, you're focused on getting a page live and your first donors in — not on an exit scenario that feels years away. But a donor list built over three, five, or ten years of ministry is one of the most valuable assets a missionary has: real relationships, real trust, real giving history. Losing access to it because of a platform's fine print is a preventable loss, not an unlucky one.</p>

                    <h2>Three questions to ask before you sign up</h2>

                    <ul>
                        <li><strong>Can I export my full donor list myself, any time, with no approval needed?</strong> A real "yes" means a self-serve CSV export in your account settings — not a support ticket that may or may not get answered.</li>
                        <li><strong>What happens to my data if I close my account?</strong> Some platforms delete everything on closure; others retain it indefinitely. Either can be a problem depending on your situation — know which one you're agreeing to.</li>
                        <li><strong>What happens if the platform is acquired or shuts down?</strong> Fundraising platforms get bought, merged, and sometimes discontinued. Ask what the data-portability commitment is in that scenario, not just in the normal course of business.</li>
                    </ul>

                    <h2>Your sending organization may already have an answer</h2>

                    <p>Some sending agencies route all support through their own internal system, where the agency — not the individual missionary — is the account owner of the donor relationship. Others give missionaries a platform where the missionary's own account holds the data. Neither model is automatically wrong, but they lead to very different outcomes if a missionary changes fields, changes agencies, or leaves the field entirely. Ask your agency directly which model applies to you, ideally before your first newsletter goes out, not after your fifth year.</p>

                    <h2>What "data ownership" looks like in practice</h2>

                    <p>Concretely, look for a platform that lets you:</p>

                    <ul>
                        <li>Export names, emails, and giving history to a spreadsheet whenever you want, without a support request.</li>
                        <li>See a written data-retention and export policy, not just a verbal assurance from a sales conversation.</li>
                        <li>Keep your account and donor relationships intact even if you change how support is processed in the future.</li>
                    </ul>

                    <p>This is one of the questions our own <a href="/resources/missions-pastor-checklist-vetting-fundraising-platforms">missions pastor checklist</a> covers when a church committee is vetting a platform on a missionary's behalf — it's worth asking for yourself even if no one else on your team is asking it.</p>

                    <h2>A five-minute check, before you build years on top of it</h2>

                    <p>You don't need to distrust every platform you consider — most reputable ones will answer this clearly and in writing. The point is simply to ask before you commit years of donor relationships to a system, rather than discovering the answer the day you need to leave. If a platform can't give you a clear, written answer to "can I export my own donor list, myself, any time," treat that as the answer.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Can I export my donor list if I switch fundraising platforms?</strong><br />
                    It depends on the platform — ask in writing, before you sign up, whether you can self-serve export your full list at any time with no approval required.</p>

                    <p><strong>Does my sending organization own my donor data instead of me?</strong><br />
                    Sometimes — it depends on whether your agency processes giving through its own internal system or gives you an independently owned account. Ask directly.</p>

                    <p><strong>Why does this matter if I'm not planning to leave?</strong><br />
                    Plans change more than expected, and a donor list built over years is one of the hardest things to rebuild. A five-minute check up front protects it.</p>

                    <p><strong>What should I ask a platform about data ownership?</strong><br />
                    Whether you can export your list yourself at any time, what happens to your data if you close your account, and what happens if the platform is acquired or shuts down.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/missions-pastor-checklist-vetting-fundraising-platforms">A Missions Pastor's Checklist: Vetting Fundraising Platforms</a></li>
                        <li><a href="/resources/true-cost-missionary-fundraising-platforms-2026">The True Cost of Every Missionary Fundraising Platform in 2026</a></li>
                        <li><a href="/resources/gofundme-alternatives-for-missionaries">GoFundMe Alternatives for Missionaries: 7 Platforms Compared</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this because "who owns the donor list" is the question we get asked most often by missionaries comparing platforms — and it's rarely covered anywhere before someone signs up.</p>
                    <p><small><em>Last updated: 2026-08-05.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/who-owns-your-donor-data-missionary-fundraising" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
